/* ==========================================================================
   Taana — facets.js
   Collection & search filtering, sorting and load-more via the Section
   Rendering API. URLs stay shareable; back/forward works; no-JS falls back
   to normal links and form submits.
   ========================================================================== */
(() => {
  const Theme = (window.Theme = window.Theme || {});
  const desktop = window.matchMedia('(min-width: 990px)');

  /* ---------- Filter drawer / sidebar ---------- */
  class FacetDrawer extends Theme.OverlayPanel {
    connectedCallback() {
      super.connectedCallback();
      this.applyMode = () => {
        const inline = this.dataset.mode === 'sidebar' && desktop.matches;
        this.classList.toggle('is-inline', inline);
        if (inline) {
          if (this.isOpen) this.close();
          this.removeAttribute('aria-hidden');
          this.removeAttribute('aria-modal');
          this.setAttribute('role', 'region');
        } else {
          this.setAttribute('role', 'dialog');
          this.setAttribute('aria-modal', 'true');
          if (!this.isOpen) this.setAttribute('aria-hidden', 'true');
        }
      };
      desktop.addEventListener('change', this.applyMode);
      this.applyMode();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      desktop.removeEventListener('change', this.applyMode);
    }
    open(opener) {
      if (this.classList.contains('is-inline')) return;
      super.open(opener);
    }
  }
  if (!customElements.get('facet-drawer')) customElements.define('facet-drawer', FacetDrawer);

  /* ---------- Filtering controller ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.onChange = (e) => {
        if (e.target.closest('[data-facet-sort]')) {
          const mirror = this.querySelector('[data-sort-mirror]');
          if (mirror) mirror.value = e.target.value;
          this.submit(0);
          return;
        }
        if (!e.target.closest('[data-facet-form]')) return;
        this.submit(e.target.type === 'number' ? 700 : 350);
      };
      this.onClick = (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) {
          e.preventDefault();
          this.render(new URL(link.href, window.location.origin).searchParams.toString());
          return;
        }
        const more = e.target.closest('[data-load-more]');
        if (more) {
          e.preventDefault();
          this.loadMore(more);
          return;
        }
        const showMore = e.target.closest('[data-facet-more]');
        if (showMore) {
          const list = showMore.previousElementSibling;
          const expanded = list.classList.toggle('is-expanded');
          showMore.setAttribute('aria-expanded', String(expanded));
          if (!showMore.dataset.more) showMore.dataset.more = showMore.textContent;
          showMore.textContent = expanded ? showMore.dataset.less : showMore.dataset.more;
          return;
        }
        const density = e.target.closest('[data-density]');
        if (density) this.setDensity(density.dataset.density, true);
      };
      this.onPop = () => this.render(window.location.search.slice(1), false);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', (e) => { if (e.target.type === 'number') this.onChange(e); });
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPop);
      try {
        const saved = localStorage.getItem('taana:grid-density');
        if (saved) this.setDensity(saved, false);
      } catch (e) { /* storage unavailable */ }
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPop);
      clearTimeout(this.timer);
    }
    get form() {
      return this.querySelector('[data-facet-form]');
    }
    params() {
      const params = new URLSearchParams();
      if (this.form) {
        for (const [k, v] of new FormData(this.form).entries()) {
          if (v !== '') params.append(k, v);
        }
      }
      const sort = this.querySelector('[data-facet-sort]');
      if (sort) params.set('sort_by', sort.value);
      const q = new URLSearchParams(window.location.search).get('q');
      if (q && !params.has('q')) params.set('q', q);
      const type = new URLSearchParams(window.location.search).get('type');
      if (type && !params.has('type')) params.set('type', type);
      return params.toString();
    }
    submit(delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.render(this.params()), delay);
    }
    async fetchSection(query) {
      const key = query;
      if (this.cache.has(key)) return this.cache.get(key);
      const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      const res = await fetch(url);
      const html = await res.text();
      this.cache.set(key, html);
      return html;
    }
    async render(query, push = true) {
      const results = document.getElementById(`ProductResults-${this.sectionId}`);
      results && results.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');
      try {
        const html = await this.fetchSection(query);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const openFacets = Array.from(this.querySelectorAll('.facet details[open]')).map((d) => d.closest('.facet').dataset.facet);
        const expandedLists = Array.from(this.querySelectorAll('.facet__list.is-expanded')).map((l) => l.closest('.facet').dataset.facet);
        [`ProductResults-${this.sectionId}`, `FacetBody-${this.sectionId}`, `ProductCount-${this.sectionId}`, `ProductCountBanner-${this.sectionId}`].forEach((id) => {
          const fresh = doc.getElementById(id);
          const current = document.getElementById(id);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        const freshToggle = doc.querySelector('.collection__filter-toggle');
        const toggle = this.querySelector('.collection__filter-toggle');
        if (freshToggle && toggle) toggle.innerHTML = freshToggle.innerHTML;
        // Preserve which filter groups the shopper had open.
        this.querySelectorAll('.facet').forEach((f) => {
          const d = f.querySelector('details');
          if (d && openFacets.includes(f.dataset.facet)) d.open = true;
          if (expandedLists.includes(f.dataset.facet)) {
            const list = f.querySelector('.facet__list');
            list && list.classList.add('is-expanded');
          }
        });
        if (push) window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        this.setDensity(this.density, false);
        const section = this.closest('.shopify-section');
        if (section && Theme.motion) Theme.motion.init(section);
        document.dispatchEvent(new CustomEvent('taana:layout-change'));
      } catch (e) {
        Theme.toast && Theme.toast(Theme.strings.cartError, { tone: 'error' });
      } finally {
        results && results.removeAttribute('aria-busy');
        this.classList.remove('is-loading');
      }
    }
    async loadMore(button) {
      button.classList.add('is-loading');
      try {
        const url = new URL(button.href, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const html = await (await fetch(url.toString())).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = document.getElementById(`ProductGrid-${this.sectionId}`);
        const freshGrid = doc.getElementById(`ProductGrid-${this.sectionId}`);
        if (grid && freshGrid) {
          const items = Array.from(freshGrid.children);
          const firstNew = items[0];
          grid.append(...items);
          const link = firstNew && firstNew.querySelector('a[href]');
          link && link.focus({ preventScroll: true });
        }
        const wrap = document.getElementById(`LoadMore-${this.sectionId}`);
        const freshWrap = doc.getElementById(`LoadMore-${this.sectionId}`);
        if (wrap) {
          if (freshWrap) wrap.innerHTML = freshWrap.innerHTML;
          else wrap.remove();
        }
        url.searchParams.delete('section_id');
        window.history.replaceState({}, '', url.toString());
        const section = this.closest('.shopify-section');
        if (section && Theme.motion) Theme.motion.init(section);
        document.dispatchEvent(new CustomEvent('taana:layout-change'));
      } catch (e) {
        window.location.href = button.href;
      }
    }
    setDensity(cols, save) {
      if (!cols) return;
      this.density = cols;
      const grid = document.getElementById(`ProductGrid-${this.sectionId}`);
      if (grid) grid.style.setProperty('--cols', cols);
      this.querySelectorAll('[data-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.density === String(cols))));
      if (save) {
        try { localStorage.setItem('taana:grid-density', cols); } catch (e) { /* storage unavailable */ }
      }
    }
  }
  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
